import React, { useState } from "react";
import ReactModal from "react-modal";
import ActivateUser from "../../components/user/ActivateUserModal";
import LoadingSpinner from "../../components/LoadingSpinner";
import EmptyRows from "../../components/reusableComponent/EmptyRows";
import {
  Pagination,
  PaginationItem,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  useMediaQuery,
} from "@mui/material";
import SortingTableUsers from "../../components/reusableComponent/SortingTableUsers";

// Modal styling
const ActivateModalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    border: "2px solid #dde",
    maxWidth: "400px",
    margin: "auto",
    Overflow: "hidden",
    OverflowY: "auto",
  },
};

interface DeactivateProps {
  getRandomColor: () => string;
  getInitials: (name: string) => string;
  capitalizeFirstLetter: (str: string) => string;
  formatDate: (date: string) => string;
  refetch: () => void;
  totalPages: number;
  getDeactiveUsersData: any[];
  deactiveCurrentPage: number;
  setCurrentPage: (page: number) => void;
  usersLoading: boolean;
  sortingLoading: boolean;
  pageSize: number;
  setPageSize: (size: number) => void;
  currentPage: any;
  setLiftingColumn: (column: string) => void;
}

const Deactivate: React.FC<DeactivateProps> = ({
  getRandomColor,
  getInitials,
  capitalizeFirstLetter,
  formatDate,
  refetch,
  totalPages,
  getDeactiveUsersData,
  deactiveCurrentPage,
  setCurrentPage,
  usersLoading,
  sortingLoading,
  pageSize,
  setPageSize,
  currentPage,
  setLiftingColumn,
}) => {
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);
  const [selectedActivateId, setSelectedActivateId] = useState(null);
  const isLargeScreen = useMediaQuery("(max-width:825px)");
  const isSmallScreen = useMediaQuery("(max-width:575px)");
  const isPageScreen = useMediaQuery("(max-width:430px)");
  const tableHeader = ["Sr no.", "User", "User id", "Created date", "Last updated date", "Role", "Branch user", "Action"];

  // Page size options
  const pageSizeOptions = [10, 25, 50, 100];

  // Open modal
  const openActivateModal = () => setIsActivateModalOpen(true);

  // Close modal
  const closeActivateModal = () => setIsActivateModalOpen(false);

  // Handle modal activation
  const isActivateModal = (data: any) => {
    setSelectedActivateId(data);
    openActivateModal();
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  // Calculate serial number based on current page and page size
  const getSerialNumber = (index: number) => {
    return (deactiveCurrentPage - 1) * pageSize + index + 1;
  };

  // Calculate the range of items being displayed
  const startItem = (deactiveCurrentPage - 1) * pageSize + 1;
  const endItem = Math.min(
    deactiveCurrentPage * pageSize,
    getDeactiveUsersData?.length || 0
  );
  const totalItems = getDeactiveUsersData?.length || 0;
  const fontSize = isLargeScreen ? "13px" : "15px";

  /* ── shared td style (white card look) ── */
  const tdBase: React.CSSProperties = {
    padding: "12px 16px",
    whiteSpace: "nowrap",
    fontSize,
    verticalAlign: "middle",
    backgroundColor: "#ffffff",
    boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
    borderTop: "1px solid #f0f0f0",
    borderBottom: "1px solid #f0f0f0",
  };

  const tdFirst: React.CSSProperties = {
    ...tdBase,
    paddingLeft: "20px",
    borderLeft: "1px solid #f0f0f0",
    borderRadius: "6px 0 0 6px",
  };

  const tdLast: React.CSSProperties = {
    ...tdBase,
    borderRight: "1px solid #f0f0f0",
    borderRadius: "0 6px 6px 0",
    textAlign: "center",
  };

  return (
    <>
      {/* ── Scrollable table container ── */}
      <div
        style={{ overflow: "auto", height: "calc(100vh - 320px)" }}
      >
        <table
          style={{
            width: "max-content",
            minWidth: "100%",
            borderCollapse: "separate",
            borderSpacing: "0 6px",   /* vertical gap between rows = card gap */
          }}
        >
          {/* ════ HEADER ════ */}
          <thead>
            <tr>
              {tableHeader.map((el, index) => (
                <th
                  key={index}
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: "#E4E4E4",
                    padding: "10px 16px",
                    paddingLeft: index === 0 ? "20px" : "16px",
                    whiteSpace: "nowrap",
                    textAlign: el === "Action" ? "center" : "left",
                    fontSize,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                    borderRadius:
                      index === 0
                        ? "6px 0 0 6px"
                        : index === tableHeader.length - 1
                        ? "0 6px 6px 0"
                        : "0",
                    minWidth:
                      el === "Action"         ? "120px"
                      : el === "Sr no."       ? "80px"
                      : el === "User"         ? "220px"
                      : el === "User id"     ? "100px"
                      : el === "Created date" ? "180px"
                      : el === "Last updated date" ? "180px"
                      : el === "Role"         ? "120px"
                      : el === "Branch user" ? "120px"
                      :                         "150px",
                  }}
                >
                  <strong
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      justifyContent: el === "Action" ? "center" : "flex-start",
                    }}
                  >
                    {el}
                    {el !== "Branch user" &&
                     el !== "Role" &&
                     el !== "Last updated date" &&
                     el !== "Sr no." &&
                     el !== "Action" && (
                      <SortingTableUsers
                        index={
                          el === "User"
                            ? "username"
                            : el === "Created date"
                              ? "created"
                              : el === "User id"
                                ? "id"
                                : el
                        }
                        setLiftingColumn={setLiftingColumn}
                      />
                    )}
                  </strong>
                </th>
              ))}
            </tr>
          </thead>

          {/* ════ BODY ════ */}
          <tbody>
            {usersLoading || sortingLoading ? (
              <tr>
                <td colSpan={8} style={{ padding: "40px", textAlign: "center" }}>
                  <LoadingSpinner />
                </td>
              </tr>
            ) : !getDeactiveUsersData?.length ? (
              <tr>
                <td colSpan={8}>
                  <EmptyRows value="" />
                </td>
              </tr>
            ) : (
              getDeactiveUsersData.map((card: any, index: number) => (
                <tr
                  key={card.id || index}
                  style={{ cursor: "pointer" }}
                  onMouseEnter={(e) => {
                    Array.from(e.currentTarget.cells).forEach(
                      (td) => ((td as HTMLElement).style.backgroundColor = "#f9f9f9")
                    );
                  }}
                  onMouseLeave={(e) => {
                    Array.from(e.currentTarget.cells).forEach(
                      (td) => ((td as HTMLElement).style.backgroundColor = "#ffffff")
                    );
                  }}
                >
                  {/* Sr no. */}
                  <td style={tdFirst}>
                    <label style={{ fontSize, marginBottom: 0 }}>
                      {getSerialNumber(index)}
                    </label>
                  </td>

                  {/* User */}
                  <td style={tdBase}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <div className="symbol symbol-25px symbol-circle fs-2">
                        <span
                          className={`symbol-label ${getRandomColor()} text-inverse-warning fw-bold`}
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 12,
                            flexShrink: 0,
                          }}
                        >
                          {getInitials(card.username)}
                        </span>
                      </div>
                      <label style={{ fontSize, marginBottom: 0 }}>
                        {capitalizeFirstLetter(card.username.split("@")[0])}
                      </label>
                    </div>
                  </td>

                  {/* User id */}
                  <td style={tdBase}>
                    <label style={{ fontSize, marginBottom: 0 }}>
                      {card.id}
                    </label>
                  </td>

                  {/* Created date */}
                  <td style={tdBase}>
                    <label style={{ fontSize, marginBottom: 0 }}>
                      {formatDate(card.created)}
                    </label>
                  </td>

                  {/* Last updated date */}
                  <td style={tdBase}>
                    <label style={{ fontSize, marginBottom: 0 }}>
                      {card.last_updated ? formatDate(card.last_updated) : ""}
                    </label>
                  </td>

                  {/* Role */}
                  <td style={tdBase}>
                    <label style={{ fontSize, marginBottom: 0 }}>
                      {capitalizeFirstLetter(card.role)}
                    </label>
                  </td>

                  {/* Branch user */}
                  <td style={tdBase}>
                    <label style={{ fontSize, marginBottom: 0 }}>
                      {card?.branch_user === true ? (
                        <span style={{ color: "green" }}>Yes</span>
                      ) : (
                        <span style={{ color: "red" }}>No</span>
                      )}
                    </label>
                  </td>

                  {/* Action */}
                  <td style={tdLast}>
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <button
                        className="btn btn-user-primary me-2"
                        title="Activate"
                        onClick={(event) => {
                          event.stopPropagation();
                          isActivateModal(card);
                        }}
                      >
                        Activate
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ReactModal
        isOpen={isActivateModalOpen}
        onRequestClose={closeActivateModal}
        style={ActivateModalStyles}
        contentLabel="Activate User"
        shouldCloseOnOverlayClick={false}
      >
        <ActivateUser
          closeActivateModal={closeActivateModal}
          userData={selectedActivateId}
          refetch={refetch}
        />
      </ReactModal>

      {getDeactiveUsersData?.length > 0 && (
        <>
          {/* Pagination Controls */}
          <div className={`d-flex justify-content-between align-items-center mt-4 px-3 mb-2 ${isPageScreen ? "flex-column gap-3" : ""}`}>
            {/* Left Side: Rows per page + range info */}
            <div className="d-flex align-items-center gap-4">
              <div className="d-flex align-items-center gap-2">
                <span style={{ fontSize: isSmallScreen ? "12px" : "14px", whiteSpace: "nowrap" }}>Show</span>
                <select
                  className="form-select form-select-sm"
                  style={{ width: "60px", fontSize: isSmallScreen ? "12px" : "14px", padding: isSmallScreen ? "2px 4px" : "2px 6px" }}
                  value={pageSize}
                  onChange={handlePageSizeChange}
                >
                  {pageSizeOptions.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <span style={{ fontSize: isSmallScreen ? "12px" : "14px", fontWeight: 500, color: "#495057" }}>
                  per page
                </span>
              </div>
            </div>

            {/* Right Side: Pagination */}
            <div style={{ position: "sticky", bottom: 0, zIndex: 10 }}>
              <div className="d-flex align-items-center">
                <Pagination
                  sx={{
                    display: "flex",
                    justifyContent: "end",
                    "& .MuiPaginationItem-root": {
                      fontSize: isSmallScreen ? "11px" : "14px",
                      minWidth: isSmallScreen ? "28px" : "36px",
                      height: isSmallScreen ? "28px" : "36px",
                      margin: isSmallScreen ? "0 1px" : "0 2px",
                      borderRadius: "6px",
                      border: "1px solid #e5e7eb",
                      color: "#374151",
                      "&:hover": {
                        backgroundColor: "#f9fafb",
                        borderColor: "#d1d5db",
                      },
                      "&.Mui-selected": {
                        backgroundColor: "#6366f1",
                        color: "#ffffff",
                        borderColor: "#6366f1",
                        "&:hover": {
                          backgroundColor: "#4f46e5",
                          borderColor: "#4f46e5",
                        },
                      },
                    },
                  }}
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                  renderItem={(item) => {
                    if (item.type === "page") {
                      const isNearCurrent =
                        Math.abs(item.page - currentPage) <= 1 ||
                        item.page === 1 ||
                        item.page === totalPages;
                      return isNearCurrent ? <PaginationItem {...item} /> : null;
                    }
                    return <PaginationItem {...item} />;
                  }}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Deactivate;